import { StateCreator } from 'zustand';
import { createWithEqualityFn } from 'zustand/traditional';

import {
  getGameRecords as restGetGameRecords,
  getChallengeRecords as restGetChallengeRecords,
  getAlbumSummaries as restGetAlbumSummaries,
  moveInAlbumSummaryPath as restMoveInAlbumSummaryPath,
  updateAlbumSummary as restUpdateAlbumSummary,
  getSpaceSummaries as restGetSpaceSummaries,
  updateSpaceSummary as restUpdateSpaceSummary,
  getGameOpponents as restGetGameOpponents,
  syncGameRecords as restSyncGameRecords,
} from '../../api/Rest/space';

import {
  createStoreListFieldHook,
  createStoreMapFieldHook,
  StoreLifecycle,
  StoreLifecycleAction,
} from '../../models/Store';
import {
  AlbumSummary,
  AlbumSummaryRepository,
  ChallengeRecord,
  ChallengeRecordRepository,
  GameRecord,
  GameRecordRepository,
  RecordStoreAction,
  RecordStoreState,
  SpaceSummaryRepository,
} from '../../models/RecordStore';
import { Profile } from '../../models/UserStore';
import { useUserStore } from '../../stores/UserStore';
import * as State from './state';
import { SpaceSummary } from '../../models/SpaceStore';

type Store = StoreLifecycleAction & RecordStoreState & RecordStoreAction;

const lifecycle = new StoreLifecycle('Recordtore');
lifecycle.finishHydration();

const initializer: StateCreator<Store> = (set, get) => {
  const initialize = lifecycle.createInitialize(
    () => {
      // do nothing
    },
    () => set((state) => ({ ...state })),
  );

  const shutdown = lifecycle.createShutdown(
    () => {
      // clear data
      const state = {
        gameRecords: new GameRecordRepository(),
        challengeRecords: new ChallengeRecordRepository(),
        albumSummaries: new AlbumSummaryRepository(),
        spaceSummaries: new SpaceSummaryRepository(),
      };
      state.gameRecords.onElementPrune = onGameRecordPrune;
      set(state);
    },
    () => set((state) => ({ ...state })),
  );

  const savePlayingRecord: RecordStoreAction['savePlayingRecord'] = (
    playingRecord,
  ) => {
    set((state) => {
      return { playingRecord: { ...state.playingRecord, ...playingRecord } };
    });
  };

  const syncPlayingRecord: RecordStoreAction['syncPlayingRecord'] =
    async () => {
      const { playingRecord } = get();
      if (playingRecord) {
        const promises: Promise<unknown>[] = [];
        if (playingRecord.gameRecord) {
          promises.push(syncGameRecord(playingRecord.gameRecord, true));
        }
        clearPlayingRecord();
        await Promise.all(promises);
      }
    };

  const clearPlayingRecord: RecordStoreAction['clearPlayingRecord'] = () => {
    set({ playingRecord: undefined });
  };

  const getGameRecords: RecordStoreAction['getGameRecords'] = (fieldName) => {
    return State.getGameRecords(get(), fieldName);
  };

  const getGameRecord: RecordStoreAction['getGameRecord'] = (gameRecordId) =>
    State.getGameRecord(get(), gameRecordId);

  const fetchGameRecords: RecordStoreAction['fetchGameRecords'] = async (
    { fieldName, classroomId, playedBy, gameSetId, gameId },
    cursor,
    options,
  ) => {
    const { ok, data } = await restGetGameRecords(
      { classroomId },
      { playedBy, gameSetId, gameId, cursor: cursor },
      options,
    );
    if (ok) {
      const newCursor = data.cursor ?? null;
      set((state) => {
        const gameRecords = data.gameRecords.map((c) => new GameRecord(c));
        if (cursor === undefined) {
          State.setGameRecords(state, fieldName, ...gameRecords);
        } else {
          State.pushGameRecords(state, fieldName, ...gameRecords);
        }
        State.setGameRecordCursor(state, fieldName, newCursor);
        return { ...state };
      });
      return {
        shouldFetchMore: !!newCursor && data.gameRecords.length === 0,
      };
    }
    return { shouldFetchMore: false };
  };

  const syncGameRecord: RecordStoreAction['syncGameRecord'] = async (
    gameRecordJson,
    isAsync,
  ) => {
    // FIXME: check if field stale?

    if (isAsync) {
      // setGameRecord(
      //   new GameRecord({
      //     ...gameRecordJson,
      //     result: undefined,
      //     evaluation: undefined,
      //   }),
      // );
    }

    const result = await restSyncGameRecords({
      gameRecords: [
        {
          ...gameRecordJson,
          result: undefined, // not uploading result because of attachment handling
        },
      ],
    });
    const record = result.data?.gameRecords[0];
    if (!record) {
      throw new Error('something went wrong');
    }
    if ('errorMessage' in record) {
      throw new Error(record.errorMessage);
    }

    // try {
    // } catch (error) {
    //   if (error instanceof Error) {
    //     console.error('syncGameRecord', error.message);
    //   }
    // }
  };

  const getChallengeRecords: RecordStoreAction['getChallengeRecords'] = (
    fieldName,
  ) => {
    return State.getChallengeRecords(get(), fieldName);
  };

  const getChallengeRecord: RecordStoreAction['getChallengeRecord'] = (
    gameRecordId,
  ) => State.getChallengeRecord(get(), gameRecordId);

  const fetchChallengeRecords: RecordStoreAction['fetchChallengeRecords'] =
    async ({ fieldName, classroomId, gameSetId }, cursor, options) => {
      const { ok, data } = await restGetChallengeRecords(
        { classroomId },
        { gameSetId, cursor: cursor },
        options,
      );
      if (ok) {
        const newCursor = data.cursor ?? null;
        set((state) => {
          const challengeRecords = data.challengeRecords.map(
            (c) => new ChallengeRecord(c),
          );
          if (cursor === undefined) {
            State.setChallengeRecords(state, fieldName, ...challengeRecords);
          } else {
            State.pushChallengeRecords(state, fieldName, ...challengeRecords);
          }
          State.setChallengeRecordCursor(state, fieldName, newCursor);
          return { ...state };
        });
        return {
          shouldFetchMore: !!newCursor && data.challengeRecords.length === 0,
        };
      }
      return { shouldFetchMore: false };
    };

  const getAlbumSummary: RecordStoreAction['getAlbumSummary'] = (gameSetId) =>
    State.getAlbumSummary(get(), gameSetId);

  const fetchAlbumSummaries: RecordStoreAction['fetchAlbumSummaries'] = async (
    { gameSetIds },
    options,
  ) => {
    const { ok, data } = await restGetAlbumSummaries(
      { gameSetIds: gameSetIds },
      options,
    );
    if (ok) {
      set((state) => {
        gameSetIds.forEach((gameSetId) => {
          const albumSummary = data.gameSetSummaries.find((summary) => {
            return gameSetId === summary.gameSetId;
          });
          if (albumSummary) {
            State.addAlbumSummaryToMap(
              state,
              gameSetId,
              new AlbumSummary(albumSummary),
            );
          }
        });
        return { ...state };
      });
    }
  };

  const updateAlbumSummary: RecordStoreAction['updateAlbumSummary'] = async (
    classroomId,
    gameSetId,
  ) => {
    const { ok, data, error } = await restUpdateAlbumSummary({
      classroomId,
      gameSetId,
    });
    if (ok) {
      set((state) => {
        State.addAlbumSummaryToMap(
          state,
          gameSetId,
          new AlbumSummary(data.gameSetSummary),
        );
        return { ...state };
      });
    } else {
      console.error(error.message);
    }
  };

  const moveInAlbumSummaryPath: RecordStoreAction['moveInAlbumSummaryPath'] =
    async (classroomId, gameSetId, moveTo) => {
      const { ok, data, error } = await restMoveInAlbumSummaryPath(
        { classroomId, gameSetId },
        { moveTo },
      );
      if (ok) {
        set((state) => {
          State.addAlbumSummaryToMap(
            state,
            gameSetId,
            new AlbumSummary(data.gameSetSummary),
          );
          return { ...state };
        });
      } else {
        console.error(error.message);
      }
    };

  const getSpaceSummary: RecordStoreAction['getSpaceSummary'] = (classroomId) =>
    State.getSpaceSummary(get(), classroomId);

  const fetchSpaceSummaries: RecordStoreAction['fetchSpaceSummaries'] = async (
    { classroomIds },
    options,
  ) => {
    const { ok, data } = await restGetSpaceSummaries(
      { classroomIds: classroomIds },
      options,
    );
    if (ok) {
      set((state) => {
        classroomIds.forEach((classroomId) => {
          const spaceSummary = data.classroomSummaries.find((summary) => {
            return classroomId === summary.classroomId;
          });
          if (spaceSummary) {
            State.addSpaceSummaryToMap(
              state,
              classroomId,
              new SpaceSummary(spaceSummary),
            );
          }
        });
        return { ...state };
      });
    }
  };

  const updateSpaceSummary: RecordStoreAction['updateSpaceSummary'] = async (
    classroomId,
  ) => {
    const { ok, data, error } = await restUpdateSpaceSummary({
      classroomId,
    });
    if (ok) {
      set((state) => {
        State.addSpaceSummaryToMap(
          state,
          classroomId,
          new SpaceSummary(data.classroomSummary),
        );
        return { ...state };
      });
    } else {
      console.error(error.message);
    }
  };

  // opponents
  const fetchGameOpponents: RecordStoreAction['fetchGameOpponents'] = async (
    classroomId,
    gameSetId,
    gameId,
    limit = 1,
  ) => {
    const { ok, data, error } = await restGetGameOpponents(
      { classroomId },
      { gameSetId, gameId, limit, withProfiles: true },
      { headers: { 'cache-control': 'no-cache' } },
    );
    if (ok) {
      useUserStore
        .getState()
        .addProfiles(
          'gamePlay',
          ...data.profiles.map((profile) => new Profile(profile.profile)),
        );
      return data;
    } else {
      console.error(error.message);
      return {
        opponents: [],
        profiles: [],
      };
    }
  };

  const onGameRecordPrune: RecordStoreAction['onGameRecordPrune'] = (game) => {
    set((state) => {
      return { ...state };
    });
  };

  return {
    initialize,
    shutdown,
    playingRecord: undefined,
    gameRecords: new GameRecordRepository(),
    challengeRecords: new ChallengeRecordRepository(),
    albumSummaries: new AlbumSummaryRepository(),
    spaceSummaries: new SpaceSummaryRepository(),
    savePlayingRecord,
    syncPlayingRecord,
    clearPlayingRecord,
    getGameRecords,
    getGameRecord,
    fetchGameRecords,
    syncGameRecord,
    getChallengeRecords,
    getChallengeRecord,
    fetchChallengeRecords,
    getAlbumSummary,
    fetchAlbumSummaries,
    updateAlbumSummary,
    moveInAlbumSummaryPath,
    getSpaceSummary,
    fetchSpaceSummaries,
    updateSpaceSummary,
    fetchGameOpponents,
    onGameRecordPrune,
  };
};

export const useRecordStore = createWithEqualityFn<Store>()(
  initializer,
  Object.is,
);
export const useRecordStoreListField = createStoreListFieldHook(useRecordStore);
export const useRecordStoreMapField = createStoreMapFieldHook(useRecordStore);
